import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { SelectButton } from 'primereact/selectbutton';
import { InputText } from 'primereact/inputtext';
import { Calendar } from '../../components/calendar';
import { Dialog } from 'primereact/dialog';
import { saveAs } from 'file-saver';

import { pesquisarProdutos, importarPlanilhaProdutos, mensagemErro } from '../tributosApi';
import { gerarPlanilha, lerPlanilha, linhasDeProdutos } from '../planilhaTributos';
import { SituacaoProdutoTag, formatarNcm, dateToIso } from '../util';

const OPCOES_CONFERIDO = [
  { label: 'Todos', value: 'T' },
  { label: 'Conferidos', value: 'S' },
  { label: 'Não conferidos', value: 'N' },
];

export default function Produtos() {
  const navigate = useNavigate();
  const [codigoEan, setCodigoEan] = useState('');
  const [descricao, setDescricao] = useState('');
  const [ncm, setNcm] = useState('');
  const [cest, setCest] = useState('');
  const [periodoConferencia, setPeriodoConferencia] = useState(null);
  const [conferido, setConferido] = useState('T');
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(false);

  // ---- Planilha Excel (exportação / importação)
  const [exportVisivel, setExportVisivel] = useState(false);
  const [descricaoInicial, setDescricaoInicial] = useState('');
  const [descricaoFinal, setDescricaoFinal] = useState('');
  const [exportando, setExportando] = useState(false);
  const [importacao, setImportacao] = useState(null); // { arquivo, leitura, resultado }
  const [importando, setImportando] = useState(false);
  const inputPlanilha = useRef(null);

  const montarParams = () => {
    const soDigitos = v => String(v || '').replace(/\D/g, '');
    const inicio = periodoConferencia && periodoConferencia[0];
    const fim = periodoConferencia && (periodoConferencia[1] || periodoConferencia[0]);
    return {
      codigoEan: codigoEan.trim() || undefined,
      descricao: descricao.trim() || undefined,
      ncm: soDigitos(ncm) || undefined,
      cest: soDigitos(cest) || undefined,
      conferido: conferido === 'T' ? undefined : conferido === 'S',
      dataConferenciaInicial: inicio ? dateToIso(inicio) : undefined,
      dataConferenciaFinal: fim ? dateToIso(fim) : undefined,
    };
  };

  const carregar = async () => {
    setCarregando(true);
    try {
      setLista(await pesquisarProdutos(montarParams()));
    } catch (e) {
      toast.error(mensagemErro(e, 'Erro ao consultar produtos.'));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conferido]);

  const pesquisar = e => {
    e.preventDefault();
    carregar();
  };

  const limpar = () => {
    setCodigoEan('');
    setDescricao('');
    setNcm('');
    setCest('');
    setPeriodoConferencia(null);
  };

  const colunaIcms = p => (
    <div className="tb-cell-sub tb-mono">
      <span>{p.cstVenda ? `CST ${p.cstVenda}` : ''}{p.cstVenda && p.csosnVenda ? ' · ' : ''}{p.csosnVenda ? `CSOSN ${p.csosnVenda}` : ''}{!p.cstVenda && !p.csosnVenda ? '—' : ''}</span>
      <small>{p.icmsAliquota ? `${Number(p.icmsAliquota).toLocaleString('pt-BR')}%` : ''}{p.cfopVenda ? ` · CFOP ${p.cfopVenda}` : ''}</small>
    </div>
  );

  // ---------------------------------------------------------------- Planilha Excel
  const normalizarDescricao = v => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();

  const exportar = async () => {
    const ini = normalizarDescricao(descricaoInicial);
    const fim = normalizarDescricao(descricaoFinal);
    if (ini && fim && ini > fim) {
      toast.warn('A descrição inicial deve ser menor ou igual à descrição final.');
      return;
    }
    setExportando(true);
    try {
      // Mesmos filtros da tela (situação, EAN, descrição, NCM, CEST e período de conferência) + faixa de descrição
      const produtos = (await pesquisarProdutos(montarParams()))
        .filter(p => {
          const d = normalizarDescricao(p.descricao);
          if (ini && d < ini) return false;
          return !fim || d <= fim || d.startsWith(fim); // final inclusivo: "B" inclui tudo o que começa com B
        })
        .sort((a, b) => normalizarDescricao(a.descricao).localeCompare(normalizarDescricao(b.descricao), 'pt-BR'));
      if (!produtos.length) {
        toast.info('Nenhum produto encontrado com esses filtros.');
        return;
      }
      const buffer = await gerarPlanilha(linhasDeProdutos(produtos), 'produto');
      const agora = new Date();
      const p2 = n => String(n).padStart(2, '0');
      const nome = `eloTributos_base_${agora.getFullYear()}${p2(agora.getMonth() + 1)}${p2(agora.getDate())}_${p2(agora.getHours())}${p2(agora.getMinutes())}.xlsx`;
      saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), nome);
      toast.success(`Planilha gerada com ${produtos.length} produto(s).`);
      setExportVisivel(false);
    } catch (e) {
      toast.error(mensagemErro(e, 'Erro ao exportar a planilha.'));
    } finally {
      setExportando(false);
    }
  };

  const selecionarPlanilha = async e => {
    const arquivo = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!arquivo) return;
    setImportando(true);
    try {
      const leitura = await lerPlanilha(await arquivo.arrayBuffer());
      if (leitura.modo !== 'produto') {
        toast.error('Esta é a planilha de Recebimentos. Importe-a na tela Recebimentos.');
        return;
      }
      if (!leitura.marcadas) {
        toast.warn(`Nenhuma das ${leitura.total} linha(s) da planilha está marcada como "Conferido".`);
        return;
      }
      setImportacao({ arquivo: arquivo.name, leitura, resultado: null });
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Não foi possível ler a planilha.');
    } finally {
      setImportando(false);
    }
  };

  const confirmarImportacao = async () => {
    if (!importacao) return;
    setImportando(true);
    try {
      const resultado = await importarPlanilhaProdutos(importacao.leitura.itens);
      setImportacao(atual => ({ ...atual, resultado }));
      if (resultado.conferidos) toast.success(`${resultado.conferidos} produto(s) conferido(s) pela planilha.`);
      if (resultado.erros && resultado.erros.length) toast.warn(`${resultado.erros.length} linha(s) não foram importadas.`);
      carregar();
    } catch (err) {
      toast.error(mensagemErro(err, 'Erro ao importar a planilha.'));
    } finally {
      setImportando(false);
    }
  };

  return (
    <div className="tb-page">
      <div className="tb-page-head">
        <div>
          <h1>Base de tributos</h1>
          <p>Cadastro fiscal dos produtos por código EAN.</p>
        </div>
        <div className="tb-head-actions">
          <button type="button" className="tb-btn tb-btn-ghost" onClick={() => setExportVisivel(true)}>
            <i className="pi pi-file-excel" /> Exportar planilha
          </button>
          <button type="button" className="tb-btn tb-btn-ghost" onClick={() => inputPlanilha.current && inputPlanilha.current.click()} disabled={importando}>
            <i className={importando ? 'pi pi-spin pi-spinner' : 'pi pi-upload'} /> Importar planilha
          </button>
          <input
            ref={inputPlanilha}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
            onChange={selecionarPlanilha}
          />
          <button type="button" className="tb-btn tb-btn-primary" onClick={() => navigate('/tributos/produtos/novo')}>
            <i className="pi pi-plus" /> Novo produto
          </button>
        </div>
      </div>

      <section className="tb-card">
        <form className="tb-filters" onSubmit={pesquisar}>
          <SelectButton value={conferido} options={OPCOES_CONFERIDO} onChange={e => e.value && setConferido(e.value)} className="tb-segmented" />
          <div className="tb-filters-row">
            <span className="p-input-icon-left tb-filtro-ean">
              <i className="pi pi-qrcode" />
              <InputText value={codigoEan} onChange={e => setCodigoEan(e.target.value)} placeholder="EAN" />
            </span>
            <span className="p-input-icon-left tb-grow">
              <i className="pi pi-search" />
              <InputText value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Descrição" />
            </span>
            <InputText className="tb-filtro-curto" value={ncm} onChange={e => setNcm(e.target.value)} placeholder="NCM" maxLength={10} />
            <InputText className="tb-filtro-curto" value={cest} onChange={e => setCest(e.target.value)} placeholder="CEST" maxLength={9} />
            <Calendar
              value={periodoConferencia}
              onChange={e => setPeriodoConferencia(e.value)}
              selectionMode="range"
              dateFormat="dd/mm/yy"
              readOnlyInput
              showIcon
              showButtonBar
              placeholder="Conferido em"
              className="tb-filtro-data"
            />
            <button type="button" className="tb-btn tb-btn-ghost" onClick={limpar} title="Limpar filtros" aria-label="Limpar filtros"><i className="pi pi-filter-slash" /></button>
            <button type="submit" className="tb-btn tb-btn-primary"><i className="pi pi-filter" /> Pesquisar</button>
          </div>
        </form>

        <DataTable
          value={lista}
          loading={carregando}
          paginator
          rows={25}
          rowsPerPageOptions={[25, 50, 100]}
          dataKey="id"
          responsiveLayout="stack"
          breakpoint="900px"
          emptyMessage="Nenhum produto encontrado."
          className="tb-table"
          onRowDoubleClick={e => navigate(`/tributos/produtos/${e.data.id}`)}
        >
          <Column header="EAN" body={p => p.codigoEan || <span className="tb-sem-ean" title={`CNPJ ${p.cnpjCliente || ''}`}>Reg. {p.registroProduto || '—'}</span>} bodyClassName="tb-mono" style={{ width: '11rem' }} />
          <Column header="Descrição" field="descricao" />
          <Column header="NCM" body={p => <span className="tb-mono">{p.ncm ? formatarNcm(p.ncm) : '—'}</span>} style={{ width: '9rem' }} />
          <Column header="ICMS" body={colunaIcms} style={{ width: '12rem' }} />
          <Column header="Situação" body={p => <SituacaoProdutoTag produto={p} />} style={{ width: '11rem' }} />
          <Column header="Conferido em" field="dataConferencia" style={{ width: '9rem' }} />
          <Column
            body={p => (
              <button type="button" className="tb-btn tb-btn-ghost tb-btn-sm" onClick={() => navigate(`/tributos/produtos/${p.id}`)}>
                <i className="pi pi-pencil" /> Editar
              </button>
            )}
            style={{ width: '7rem' }}
          />
        </DataTable>
      </section>

      <Dialog
        className="tb-dialog"
        header="Exportar planilha da Base de tributos"
        visible={exportVisivel}
        onHide={() => !exportando && setExportVisivel(false)}
        style={{ width: '34rem', maxWidth: '95vw' }}
        footer={(
          <div>
            <button type="button" className="tb-btn tb-btn-ghost" onClick={() => setExportVisivel(false)} disabled={exportando}>Cancelar</button>
            <button type="button" className="tb-btn tb-btn-primary" onClick={exportar} disabled={exportando}>
              {exportando ? <i className="pi pi-spin pi-spinner" /> : <i className="pi pi-file-excel" />} Exportar
            </button>
          </div>
        )}
      >
        <p className="tb-obs">
          Exporta os produtos da base com os <strong>filtros da tela</strong> (situação, EAN, descrição, NCM, CEST e período de conferência),
          inclusive os já conferidos. Marque a coluna <strong>Conferido</strong> nas linhas revisadas e importe a planilha.
        </p>
        <div className="tb-grid">
          <div className="tb-field tb-span-6">
            <label htmlFor="expDescIniBase">Descrição inicial</label>
            <InputText id="expDescIniBase" value={descricaoInicial} onChange={e => setDescricaoInicial(e.target.value)} placeholder="Ex.: A" />
          </div>
          <div className="tb-field tb-span-6">
            <label htmlFor="expDescFimBase">Descrição final</label>
            <InputText id="expDescFimBase" value={descricaoFinal} onChange={e => setDescricaoFinal(e.target.value)} placeholder="Ex.: C" />
          </div>
        </div>
        <small className="tb-hint" style={{ whiteSpace: 'normal' }}>
          Em branco = sem limite. A descrição final inclui tudo o que começa com o texto informado (ex.: de &quot;A&quot; até &quot;C&quot; inclui &quot;CAFÉ&quot;).
        </small>
      </Dialog>

      <Dialog
        className="tb-dialog"
        header="Importar planilha da Base de tributos"
        visible={!!importacao}
        onHide={() => !importando && setImportacao(null)}
        style={{ width: '46rem', maxWidth: '96vw' }}
        footer={importacao && !importacao.resultado ? (
          <div>
            <button type="button" className="tb-btn tb-btn-ghost" onClick={() => setImportacao(null)} disabled={importando}>Cancelar</button>
            <button type="button" className="tb-btn tb-btn-primary" onClick={confirmarImportacao} disabled={importando}>
              {importando ? <i className="pi pi-spin pi-spinner" /> : <i className="pi pi-check" />} Conferir {importacao.leitura.marcadas} produto(s)
            </button>
          </div>
        ) : (
          <div>
            <button type="button" className="tb-btn tb-btn-primary" onClick={() => setImportacao(null)}>Fechar</button>
          </div>
        )}
      >
        {importacao ? (
          <>
            <p>
              Arquivo <strong>{importacao.arquivo}</strong>: {importacao.leitura.total} linha(s),{' '}
              <strong>{importacao.leitura.marcadas}</strong> marcada(s) como conferida(s).
            </p>
            {!importacao.resultado ? (
              <p className="tb-obs">
                Cada linha marcada será validada e, se estiver correta, gravada e marcada como conferida
                (os produtos conferidos são enviados à Retaguarda na próxima sincronização). As linhas com erro não são gravadas.
              </p>
            ) : (
              <p>
                <span className="tb-tag tb-tag-ok"><i className="pi pi-check" /> {importacao.resultado.conferidos} conferido(s)</span>{' '}
                {importacao.resultado.erros && importacao.resultado.erros.length ? (
                  <span className="tb-tag tb-tag-danger"><i className="pi pi-times" /> {importacao.resultado.erros.length} com erro</span>
                ) : null}
              </p>
            )}
            {importacao.leitura.avisos && importacao.leitura.avisos.length ? (
              <section className="tb-regras tb-regras-aviso">
                <header><i className="pi pi-exclamation-triangle" /> Avisos da leitura</header>
                <ul>{importacao.leitura.avisos.slice(0, 50).map(a => <li key={a} className="tb-regra-aviso">{a}</li>)}</ul>
              </section>
            ) : null}
            {importacao.resultado && importacao.resultado.erros && importacao.resultado.erros.length ? (
              <section className="tb-regras tb-regras-erro tb-import-erros">
                <header><i className="pi pi-times-circle" /> Linhas não importadas</header>
                <ul>
                  {importacao.resultado.erros.map(e => (
                    <li key={`${e.linha}-${e.id}`} className="tb-regra-erro">
                      <strong>Linha {e.linha}</strong>{e.descricao ? ` · ${e.descricao}` : ''}: {e.mensagem}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        ) : null}
      </Dialog>
    </div>
  );
}
